import { calculateHeaderGroupHeight } from '@theme/critical'
import { Component } from '@theme/component'
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities'

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
    requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop']

    /**
     * Width of window when header drawer was hidden
     * @type {number | null}
     */
    #menuDrawerHiddenWidth = null

    /**
     * An intersection observer for monitoring sticky header position
     * @type {IntersectionObserver | null}
     */
    #intersectionObserver = null

    /**
     * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
     * @type {boolean}
     */
    #offscreen = false

    /**
     * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
     * @type {number}
     */
    #lastScrollTop = 0

    /**
     * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
     * @type {number | null}
     */
    #timeout = null

    /**
     * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
     * @constant {number}
     */
    #animationDelay = 350

    /**
     * Minimum downward scroll delta (in px) required to trigger header hide.
     * Filters jitter from mobile browser address bar animations.
     * @constant {number}
     */
    #hideThreshold = 30

    /**
     * Whether a rAF update is already scheduled
     * @type {boolean}
     */
    #rafPending = false

    /**
     * Cached bottom offset of the header in its natural document position.
     * Updated on resize to avoid calling getBoundingClientRect() during scroll.
     * @type {number}
     */
    #headerNaturalTop = 0

    /**
     * Keeps the global `--header-height` custom property up to date,
     * which other theme components can then consume
     */
    #resizeObserver = new ResizeObserver(([entry]) => {
        if (!entry) return

        const { height } = entry.target.getBoundingClientRect()
        document.body.style.setProperty('--header-height', `${height}px`)

        // Cache the section's document offset — the scroll position where sticky releases
        const section = this.closest('.header-section')
        this.#headerNaturalTop = section ? section.offsetTop : 0

        // Check if the menu drawer should be hidden in favor of the header menu
        if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
            this.#updateMenuVisibility(false)
        }
    })

    /**
     * Observes the header while scrolling the viewport to track when its actively sticky
     * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
     */
    #observeStickyPosition = (alwaysSticky = true) => {
        if (this.#intersectionObserver) return

        const config = {
            threshold: alwaysSticky ? 1 : 0,
        }

        this.#intersectionObserver = new IntersectionObserver(([entry]) => {
            if (!entry) return

            const { isIntersecting } = entry

            if (alwaysSticky) {
                this.dataset.stickyState = isIntersecting ? 'inactive' : 'active'
                changeMetaThemeColor(this.refs.headerRowTop)
            } else {
                this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active'
            }
        }, config)

        this.#intersectionObserver.observe(this)
    }

    /**
     * Handles the overflow minimum event from the header menu
     * @param {OverflowMinimumEvent} event
     */
    #handleOverflowMinimum = (event) => {
        this.#updateMenuVisibility(event.detail.minimumReached)
    }

    /**
     * Updates the visibility of the menu and drawer
     * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
     */
    #updateMenuVisibility(hideMenu) {
        if (hideMenu) {
            this.refs.headerDrawerContainer.classList.remove('desktop:hidden')
            this.#menuDrawerHiddenWidth = window.innerWidth
            this.refs.headerMenu.classList.add('hidden')
        } else {
            this.refs.headerDrawerContainer.classList.add('desktop:hidden')
            this.#menuDrawerHiddenWidth = null
            this.refs.headerMenu.classList.remove('hidden')
        }
    }

    #handleWindowScroll = () => {
        if (this.#rafPending) return
        this.#rafPending = true

        requestAnimationFrame(() => {
            this.#rafPending = false
            this.#updateScrollState()
        })
    }

    #updateScrollState() {
        const stickyMode = this.getAttribute('sticky')
        if (!this.#offscreen && stickyMode !== 'always') return

        const scrollTop = document.scrollingElement?.scrollTop ?? 0
        const scrollDelta = scrollTop - this.#lastScrollTop
        this.#lastScrollTop = scrollTop

        if (stickyMode === 'always') {
            const isAtTop = this.getBoundingClientRect().top >= 0

            if (isAtTop) {
                this.dataset.scrollDirection = 'none'
            } else if (scrollDelta < 0) {
                this.dataset.scrollDirection = 'up'
            } else if (scrollDelta > 0) {
                this.dataset.scrollDirection = 'down'
            }

            return
        }

        // No animation while header is still at its natural position
        if (scrollTop <= this.#headerNaturalTop) {
            if (this.#timeout) {
                clearTimeout(this.#timeout)
                this.#timeout = null
            }
            if (this.dataset.stickyState && this.dataset.stickyState !== 'inactive') {
                this.removeAttribute('data-animating')
                this.dataset.stickyState = 'inactive'
                this.dataset.scrollDirection = 'none'
            }
            this.#offscreen = false
            return
        }

        if (scrollDelta < 0) {
            // Scrolling up — respond immediately, cancel any pending hide
            if (this.#timeout) {
                clearTimeout(this.#timeout)
                this.#timeout = null
            }
            if (this.dataset.stickyState === 'active') {
                // Already visible — cancel any pending hide
                this.removeAttribute('data-animating')
                this.dataset.scrollDirection = 'up'
            } else {
                // Header was hidden — place sticky at translateY(-100%), then slide in
                this.setAttribute('data-animating', '')
                this.dataset.stickyState = 'active'
                this.dataset.scrollDirection = 'up'

                // Force the browser to compute the translated-away state
                // before starting the transition to translateY(0)
                void this.offsetWidth
                this.removeAttribute('data-animating')
            }
        } else if (scrollDelta > this.#hideThreshold && this.dataset.stickyState === 'active') {
            // Scrolling down past threshold while header is visible — start hide sequence
            if (!this.#timeout) {
                this.dataset.scrollDirection = 'none'

                // Delay before fade-out to absorb brief scroll direction reversals
                this.#timeout = setTimeout(() => {
                    this.setAttribute('data-animating', '')

                    // After opacity transition, switch to idle state
                    this.#timeout = setTimeout(() => {
                        this.dataset.stickyState = 'idle'
                        this.removeAttribute('data-animating')
                        this.#timeout = null
                    }, this.#animationDelay)
                }, 60)
            }
        } else if (scrollDelta > this.#hideThreshold) {
            this.dataset.scrollDirection = 'none'
            this.dataset.stickyState = 'idle'
        }
    }

    connectedCallback() {
        super.connectedCallback()
        this.#resizeObserver.observe(this)
        this.addEventListener('overflowMinimum', this.#handleOverflowMinimum)

        const stickyMode = this.getAttribute('sticky')
        if (stickyMode) {
            this.#observeStickyPosition(stickyMode === 'always')

            if (stickyMode === 'scroll-up' || stickyMode === 'always') {
                document.addEventListener('scroll', this.#handleWindowScroll)
            }
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback()
        this.#resizeObserver.disconnect()
        this.#intersectionObserver?.disconnect()
        this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum)
        document.removeEventListener('scroll', this.#handleWindowScroll)
        document.body.style.setProperty('--header-height', '0px')
    }
}

if (!customElements.get('header-component')) {
    customElements.define('header-component', HeaderComponent)
}

onDocumentLoaded(() => {
    const header = document.querySelector('#header-component')
    const headerGroup = document.querySelector('#header-group')

    // Update header group height on resize of any child
    if (headerGroup) {
        const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup))

        // Observe all children of the header group
        const children = headerGroup.children
        for (let i = 0; i < children.length; i++) {
            const element = children[i]
            if (element === header || !(element instanceof HTMLElement)) continue
            resizeObserver.observe(element)
        }

        // Also observe the header group itself for child changes
        const mutationObserver = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.type === 'childList') {
                    // Re-observe all children when the list changes
                    const children = headerGroup.children
                    for (let i = 0; i < children.length; i++) {
                        const element = children[i]
                        if (element === header || !(element instanceof HTMLElement)) continue
                        resizeObserver.observe(element)
                    }
                }
            }
        })

        mutationObserver.observe(headerGroup, { childList: true })
    }
})
